import { Position } from "geojson";
import {
  buildEmptyCreateHlaWay,
  CreateHlaWay,
  HlaWayType,
  hlaWayTypeIsRoad,
} from "../../../../hla/models/HlaWay";
import turf from "../../../../shared/utils/turf";
import {
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
  HeavyVehicleFlowType,
  NumberOfLanesType,
} from "@keita/lanepatrol-sdk";
import {
  buildCreateOrUpdateHlaLane,
  CreateOrUpdateHlaLane,
  PresenceDistance,
} from "../../../../hla/models/HlaLane";

interface SideInfo {
  facilityType?: FacilityType;
  cycleLane?: "shared" | "lane" | "separate";
  lightSegregation: boolean;
  parking?: "parallel" | "perpendicular";
  sidewalk: boolean;
  building: boolean;
}

type Side = "left" | "right";

export class HlaWayBuilder {
  name: string;
  type: HlaWayType;
  coordinates: Position[];

  oneWay = false;

  left: SideInfo;
  right: SideInfo;

  roadWidth?: number;
  cycleWayWidth?: number;

  tramOrTrainRailsForward = false;
  tramOrTrainRailsBackward = false;
  nRoadLanes = 0;

  maxSpeed?: number;
  lit = true;
  busStop = false;

  rightHandTraffic: boolean;
  frontSide: Side;
  backSide: Side;

  constructor(
    name: string,
    type: HlaWayType,
    coordinates: Position[],
    rightHandTraffic: boolean,
  ) {
    this.name = name;
    this.type = type;
    this.coordinates = coordinates;

    this.rightHandTraffic = rightHandTraffic;
    this.frontSide = rightHandTraffic ? "right" : "left";
    this.backSide = rightHandTraffic ? "left" : "right";

    const info: SideInfo = {
      facilityType: undefined,
      lightSegregation: false,
      parking: undefined,
      sidewalk: false,
      building: false,
    };

    this.left = { ...info };
    this.right = { ...info };
  }

  toCreateHlaWay = (): CreateHlaWay => {
    const way = buildEmptyCreateHlaWay();

    const lineString = turf.lineString(this.coordinates);

    way.coordinates = this.coordinates;
    way.lengthM = turf.length(lineString, {
      units: "meters",
    });

    way.type = this.type;
    way.lit = this.lit;

    const inMainRoad =
      this.type === HlaWayType.Motorway ||
      this.type === HlaWayType.Primary ||
      this.type === HlaWayType.Secondary ||
      this.type === HlaWayType.Tertiary;

    way.heavyVehicleFlow =
      inMainRoad || this.busStop
        ? HeavyVehicleFlowType.MediumToHigh
        : HeavyVehicleFlowType.LowOrRestrictedAccess;

    way.frontFacingLane = this.buildLane(true);

    if (!this.oneWay || this[this.backSide].cycleLane) {
      way.backFacingLane = this.buildLane(false);
    }

    if (this.maxSpeed) {
      way.averageRoadSpeed = this.maxSpeed;
    }

    return way;
  };

  buildLane(frontFacing: boolean): CreateOrUpdateHlaLane {
    const lane = buildCreateOrUpdateHlaLane(frontFacing);

    const [nearSideName, otherSideName] = frontFacing
      ? [this.frontSide, this.backSide]
      : [this.backSide, this.frontSide];

    const nearSide = this[nearSideName];
    const otherSide = this[otherSideName];

    const typeIsRoad = hlaWayTypeIsRoad(this.type);

    // TODO flip the adjacentFeatures in the end if !rightHandTraffic

    // Facility type and width
    if (this.type === HlaWayType.Cycleway) {
      lane.facilityType = FacilityType.OffRoadBicyclePath;
    } else if (typeIsRoad) {
      if (nearSide.cycleLane === "lane") {
        lane.facilityType = FacilityType.OnRoadBicycleLane;
        lane.facilityWidthPerDirection = FacilityWidth.VeryNarrow;
      } else if (nearSide.cycleLane === "separate") {
        lane.facilityType = FacilityType.OffRoadBicyclePath;
        lane.facilityWidthPerDirection = FacilityWidth.Narrow;
        lane.lightSegregation = true;
      } else {
        lane.facilityType = FacilityType.MixedTrafficRoadLane;
        lane.facilityWidthPerDirection = FacilityWidth.Wide;
      }

      // if the flow is mixed with the road, there's a road lane to the side
      lane.adjacentFeaturesLeft.roadLane = PresenceDistance.From0to1m;
      const nRoadLanesPerDirection = this.nRoadLanes / (this.oneWay ? 1 : 2);
      lane.adjacentFeaturesLeft.numberOfLanes =
        nRoadLanesPerDirection > 1
          ? NumberOfLanesType.MoreThanOnePerDirection
          : NumberOfLanesType.OnePerDirection;
    } else if (this.type === HlaWayType.Sidewalk) {
      lane.facilityType = FacilityType.Sidewalk;
      lane.facilityWidthPerDirection = FacilityWidth.Narrow;
    } else if (
      this.type === HlaWayType.Pedestrian ||
      this.type === HlaWayType.Platform ||
      this.type === HlaWayType.FootPath
    ) {
      lane.facilityType = FacilityType.MultiUsePath;
      if (this.type === HlaWayType.Pedestrian) {
        lane.facilityWidthPerDirection = FacilityWidth.Wide;
      } else {
        lane.facilityWidthPerDirection = FacilityWidth.VeryNarrow;
      }
    }

    // Flow direction
    if (
      (typeIsRoad && nearSide.cycleLane == null) ||
      nearSide.cycleLane === "shared"
    ) {
      // if the lane is going on the road, the flow direction is shared with the road
      lane.flowDirection = this.oneWay
        ? FlowDirectionType.OneWay
        : FlowDirectionType.TwoWay;
    } else if (this.oneWay) {
      // if the lane is separate, but the road is one way, we set it as one way
      lane.flowDirection = FlowDirectionType.OneWay;
    } else {
      // if the lane is separate and two-way, we assume that the flow is one-way if there are 2 separate cycleways
      lane.flowDirection =
        nearSide.cycleLane && otherSide.cycleLane
          ? FlowDirectionType.OneWay
          : FlowDirectionType.TwoWay;
    }

    if (nearSide.parking) {
      lane.adjacentFeaturesRight.vehicleParking = PresenceDistance.From0to1m;
      if (nearSide.sidewalk) {
        lane.adjacentFeaturesRight.sidewalk = PresenceDistance.From1to3m;
        lane.adjacentFeaturesRight.objectOrLevelChange =
          PresenceDistance.From1to3m;
      }
    } else if (nearSide.sidewalk) {
      lane.adjacentFeaturesRight.sidewalk = PresenceDistance.From0to1m;
      lane.adjacentFeaturesRight.objectOrLevelChange =
        PresenceDistance.From0to1m;
    }
    if (this.roadWidth != null) {
      lane.facilityWidthPerDirection = numberToFacilityWidth(this.roadWidth);
    }
    if (this.cycleWayWidth != null) {
      lane.facilityWidthPerDirection = numberToFacilityWidth(
        this.cycleWayWidth,
      );
    }

    if (
      lane.facilityType === FacilityType.MixedTrafficRoadLane ||
      lane.facilityType === FacilityType.OnRoadBicycleLane
    ) {
      lane.tramOrTrainRails = frontFacing
        ? this.tramOrTrainRailsForward
        : this.tramOrTrainRailsBackward;
    }

    if (this.nRoadLanes === 1) {
      if (otherSide.parking) {
        lane.adjacentFeaturesLeft.vehicleParking = PresenceDistance.From1to3m;
        lane.adjacentFeaturesLeft.objectOrLevelChange =
          PresenceDistance.From1to3m;
      } else if (otherSide.sidewalk) {
        lane.adjacentFeaturesLeft.sidewalk = PresenceDistance.From1to3m;
        lane.adjacentFeaturesLeft.objectOrLevelChange =
          PresenceDistance.From1to3m;
      }
    }

    if (!this.rightHandTraffic) {
      // switch adjacent feature sides to match opposite direction
      const auxFeatures = lane.adjacentFeaturesLeft;
      lane.adjacentFeaturesLeft = lane.adjacentFeaturesRight;
      lane.adjacentFeaturesRight = auxFeatures;
    }

    return lane;
  }
}

const numberToFacilityWidth = (width: number) => {
  if (width < 1) {
    return FacilityWidth.VeryNarrow;
  } else if (width < 2) {
    return FacilityWidth.Narrow;
  } else {
    return FacilityWidth.Wide;
  }
};
